import { axiosAuthorized } from "../utils/axios";
import config from "../config/config";
import { ApiResponse } from "./authService"; // Re-using from authService

// ---- INTERFACES ---- //

export interface CreateOrderPayload {
  booking_id: string; // Required
  customer_email?: string; // Optional
  customer_phone?: string; // Optional
}

export interface CreateOrderResponseData {
  order_id: string; // Razorpay order ID
  amount: number; // Amount in paisa
  currency: string; // e.g., "INR"
  payment_id: string; // Internal payment record UUID
  key_id: string; // Razorpay key ID
}

export interface VerifyPaymentPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  payment_id: string; // Internal payment record UUID
  booking_id?: string; // Optional for reference
}

export interface VerifyPaymentResponseData {
  payment_id: string; // Internal payment record UUID
  razorpay_payment_id: string;
  razorpay_order_id: string;
  status: "created" | "authorized" | "captured" | "failed" | "refunded";
}

// ---- PAYMENT SERVICE CLASS ---- //

class PaymentService {
  private readonly paymentEndpoint = config.ENDPOINTS.PAYMENT;

  /**
   * Create a new payment order.
   * @param payload - The order details.
   */
  async createOrder(
    payload: CreateOrderPayload
  ): Promise<ApiResponse<CreateOrderResponseData>> {
    try {
      const response = await axiosAuthorized.post<
        ApiResponse<CreateOrderResponseData>
      >(`${this.paymentEndpoint}/create-order`, payload);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify a payment after it has been processed by the client.
   * @param payload - The payment verification details.
   */
  async verifyPayment(
    payload: VerifyPaymentPayload
  ): Promise<ApiResponse<VerifyPaymentResponseData>> {
    try {
      const response = await axiosAuthorized.post<
        ApiResponse<VerifyPaymentResponseData>
      >(`${this.paymentEndpoint}/verify-payment`, payload);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handles API errors in a standardized way.
   */
  private handleError(error: any): Error {
    if (error.response) {
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "An error occurred during the payment process.";
      return new Error(message);
    } else if (error.request) {
      return new Error(
        "Could not connect to the payment server. Please check your network connection."
      );
    } else {
      return new Error(error.message || "An unexpected error occurred.");
    }
  }
}

export const paymentService = new PaymentService();
export default paymentService;
